import { format, formatISO, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, addWeeks } from 'date-fns';
import { SessionData } from '../../types/sessionData';
import { StatusSession } from '../../types/enumStatusSession';
import { SessionFilterType, FilterType } from '../../types/enumSessionFilter';
import { countInclusiveDays } from '../functions/countInclusiveDays';
import apiClient from './apiClient';

export const getSessions = async (trainerId: string, filter: FilterType = SessionFilterType.CURRENT_WEEK): Promise<Array<SessionData>> => {
    try {
        const params: Record<string, string> = { trainerId };
        const now = new Date();

        if (typeof filter === 'string') {
            switch (filter) {
                case SessionFilterType.CURRENT_WEEK: {
                    const start = startOfWeek(now, { weekStartsOn: 1 });
                    const end = endOfWeek(now, { weekStartsOn: 1 });
                    params.startBefore = formatISO(end);
                    params.endAfter = formatISO(start);
                    break;
                }
                case SessionFilterType.PREVIOUS_WEEK: {
                    const start = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
                    const end = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
                    params.startBefore = formatISO(end);
                    params.endAfter = formatISO(start);
                    break;
                }
                case SessionFilterType.NEXT_WEEK: {
                    const start = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
                    const end = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
                    params.startBefore = formatISO(end);
                    params.endAfter = formatISO(start);
                    break;
                }
                case SessionFilterType.CURRENT_MONTH: {
                    params.startBefore = formatISO(endOfMonth(now));
                    params.endAfter = formatISO(startOfMonth(now));
                    break;
                }
                case SessionFilterType.ALL:
                default:
                    break;
            }
        } else if (typeof filter === 'object' && filter.type === SessionFilterType.CUSTOM_DATE && filter.customStart && filter.customEnd) {
            params.startBefore = formatISO(filter.customEnd);
            params.endAfter = formatISO(filter.customStart);
        }

        const sessionResponse = await apiClient.get('/sessions', { params });
        const rawSessions = sessionResponse.data.data;

        const responseTrainings = await apiClient.get('/trainings');
        const rawTrainings = responseTrainings.data.data;
        const trainingMap = new Map(rawTrainings.map((training: any) => [training.id, training]));

        const sessions = rawSessions.map((session: any) => {
            const trainingInfo: any = trainingMap.get(Number(session.trainingId));

            const beginDate = format(parseISO(session.startDate), 'dd/MM/yyyy');
            const endDate = format(parseISO(session.endDate), 'dd/MM/yyyy');

            const numberDays = countInclusiveDays(session.startDate, session.endDate);

            return {
                id: session.id,
                trainingLabel: trainingInfo ? trainingInfo.label : 'Pas de formation',
                sessionName: session.name,
                sessionBegin: beginDate,
                sessionEnd: endDate,
                nomberDays: numberDays,
                trainees: session.traineesId,
                sessionToken: session.token,
                typeTraining: session.type,
                societyId: session.societyId,
                closingDate: session.closingDate ? format(parseISO(session.closingDate), 'dd/MM/yyyy') : null
            };
        });

        return sessions;
    } catch (error) {
        console.error('Error fetching sessions:', error);
        return [];
    }
};

export const getSession = async (sessionId: string | undefined): Promise<SessionData | null> => {
    if (!sessionId) {
        console.error('sessionId is null or undefined');
        return null;
    }

    try {
        const responseSession = await apiClient.get('/sessions', {
            params: { id: sessionId }
        });
        const dataSession = responseSession.data.data[0];

        const responseTraining = await apiClient.get('/trainings', {
            params: { id: dataSession.trainingId }
        });
        const dataTraining = responseTraining.data.data[0];

        const responseTrainer = await apiClient.get('/users', {
            params: { id: dataSession.trainerId }
        });
        const dataTrainer = responseTrainer.data.data[0];

        const beginDate = format(parseISO(dataSession.startDate), 'dd/MM/yyyy');
        const endDate = format(parseISO(dataSession.endDate), 'dd/MM/yyyy');

        const numberDays = countInclusiveDays(dataSession.startDate, dataSession.endDate);

        return {
            id: dataSession.id,
            trainingLabel: dataTraining.label,
            sessionToken: dataSession.token,
            sessionName: dataSession.name,
            sessionBegin: beginDate,
            sessionEnd: endDate,
            nomberDays: numberDays,
            trainerId: dataSession.trainerId,
            trainerName: `${dataTrainer.firstname} ${dataTrainer.lastname}`,
            trainees: dataSession.traineesId,
            slotsId: dataSession.slotsId,
            typeTraining: dataSession.type,
            societyId: dataSession.societyId,
            closingDate: dataSession.closingDate ? format(parseISO(dataSession.closingDate), 'dd/MM/yyyy') : null
        };
    } catch (error) {
        console.error('Error getting session:', error);
        return null;
    }
};

export const getSessionByName = async (sessionName: string | undefined): Promise<string | null> => {
    if (!sessionName) {
        console.error('sessionName is null or undefined');
        return null;
    }

    try {
        const response = await apiClient.get('/sessions', {
            params: { name: sessionName }
        });
        const data = response.data.data[0];

        return data.id;
    } catch (error) {
        console.error('Error getting session by name:', error);
        return null;
    }
};

export const createSession = async (
    sessionName: string,
    trainingId: string,
    sessionBegin: string,
    sessionEnd: string,
    trainerId: string,
    typeTraining?: string,
    societyId?: string
): Promise<{ status: number }> => {
    try {
        const response = await apiClient.post('/sessions', {
            name: sessionName,
            trainingId: trainingId as string,
            startDate: sessionBegin,
            endDate: sessionEnd,
            trainerId: trainerId,
            type: typeTraining,
            societyId: societyId?.toString(),
            status: StatusSession.CONFIRMED
        });

        return { status: response.status };
    } catch (error) {
        console.error('Error creating session:', error);
        return { status: 500 };
    }
};

export const updateSession = async (sessionId: string, sessionName: string, trainingId: string, typeTraining?: string, societyId?: string): Promise<{ status: number }> => {
    try {
        const response = await apiClient.put(`/sessions/${sessionId}`, {
            name: sessionName,
            trainingId: trainingId,
            type: typeTraining,
            societyId: societyId?.toString()
        });

        return { status: response.status };
    } catch (error) {
        console.error('Error updating session:', error);
        return { status: 500 };
    }
};

export const addTraineesSession = async (sessionId: string, traineesId: Array<string>): Promise<{ status: number }> => {
    try {
        const response = await apiClient.put(`/sessions/${sessionId}/add-trainees`, {
            traineesIds: traineesId
        });

        return { status: response.status };
    } catch (error) {
        console.error('Error updating trainees session:', error);
        return { status: 500 };
    }
};

export const deleteTraineesSession = async (sessionId: string, traineesId: Array<string>): Promise<{ status: number }> => {
    try {
        const response = await apiClient.put(`/sessions/${sessionId}/remove-trainees`, {
            traineesIds: traineesId
        });

        return { status: response.status };
    } catch (error) {
        console.error('Error deleting trainees session:', error);
        return { status: 500 };
    }
};

export const closeSession = async (sessionId: string | undefined): Promise<boolean> => {
    if (!sessionId) {
        console.error('sessionId is null or undefined');
        return false;
    }

    try {
        const response = await apiClient.put(`/sessions/${sessionId}/close`);

        return response.status === 204;
    } catch (error) {
        console.error('Error closing session:', error);
        return false;
    }
};

export const openSession = async (sessionId: string | undefined): Promise<boolean> => {
    if (!sessionId) {
        console.error('sessionId is null or undefined');
        return false;
    }

    try {
        const response = await apiClient.put(`/sessions/${sessionId}/unclose`);

        return response.status === 204;
    } catch (error) {
        console.error('Error opening session:', error);
        return false;
    }
};

export const deleteSession = async (sessionId: string | undefined): Promise<boolean> => {
    if (!sessionId) {
        console.error('sessionId is null or undefined');
        return false;
    }

    try {
        const response = await apiClient.delete(`/sessions/${sessionId}`);

        return response.status === 204;
    } catch (error) {
        console.error('Error deleting session:', error);
        return false;
    }
};
